import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SearchableSelect.module.css';

export interface SearchableOption {
  id: string;
  label: string;
  subtitle?: string;
}

interface Props {
  options: SearchableOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /** When true, show "All …" as initial placeholder and allow empty selection. */
  allowEmpty?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  allowEmpty = false,
}: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Find the currently selected option's label to show in the input when not searching
  const selectedOption = options.find((o) => o.id === value);

  // Filter options by query (search both label and subtitle)
  const q = query.toLowerCase();
  const filtered = q
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.subtitle && o.subtitle.toLowerCase().includes(q)),
      )
    : options;

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Scroll focused item into view
  useEffect(() => {
    if (focusIdx >= 0 && listRef.current) {
      const item = listRef.current.children[focusIdx] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusIdx]);

  const handleSelect = useCallback(
    (id: string) => {
      onChange(id);
      setOpen(false);
      setQuery('');
      setFocusIdx(-1);
    },
    [onChange],
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        setFocusIdx(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusIdx((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusIdx((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusIdx >= 0 && focusIdx < filtered.length) {
          handleSelect(filtered[focusIdx].id);
        }
        break;
      case 'Escape':
        // The list is open here: close only the list, not an enclosing dialog.
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setQuery('');
        setFocusIdx(-1);
        break;
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <input
        ref={inputRef}
        className={styles.input}
        type="text"
        placeholder={placeholder}
        value={open ? query : (selectedOption?.label ?? (allowEmpty ? '' : ''))}
        onFocus={() => {
          setOpen(true);
          setQuery('');
          setFocusIdx(-1);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setFocusIdx(0);
          if (!open) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      {value && !open && (
        <button
          className={styles.clearBtn}
          onClick={() => {
            onChange('');
            setQuery('');
            inputRef.current?.focus();
          }}
          title={t('ui.searchableSelect.clearSelection')}
          type="button"
        >
          ×
        </button>
      )}
      {open && (
        <div className={styles.dropdown} ref={listRef}>
          {filtered.length === 0 ? (
            <div className={styles.noResults}>{t('ui.searchableSelect.noResults')}</div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                key={opt.id}
                className={`${styles.option} ${idx === focusIdx ? styles.focused : ''} ${opt.id === value ? styles.selected : ''}`}
                onClick={() => handleSelect(opt.id)}
                type="button"
              >
                <span className={styles.optionTitle}>{opt.label}</span>
                {opt.subtitle && (
                  <span className={styles.optionSubtitle}>{opt.subtitle}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
